import { useState, useEffect, useMemo } from 'react';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

async function fetchRoute(coords) {
  if (!MAPBOX_TOKEN || coords.length < 2) return null;
  const pairs = coords.map(([lat, lng]) => `${lng},${lat}`).join(';');
  try {
    const res = await fetch(
      `https://api.mapbox.com/directions/v5/mapbox/driving/${pairs}?geometries=geojson&overview=full&access_token=${MAPBOX_TOKEN}`
    );
    const data = await res.json();
    if (data.routes?.[0]) {
      return data.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]);
    }
  } catch {
    // Fall through
  }
  return null;
}

export default function useRoutes(activitiesWithCoords) {
  const [routes, setRoutes] = useState({});

  const routeKey = useMemo(
    () => activitiesWithCoords.map(a => `${a.id}:${a.day_number}:${a.position}`).join(','),
    [activitiesWithCoords]
  );

  useEffect(() => {
    if (!MAPBOX_TOKEN || activitiesWithCoords.length < 2) {
      setRoutes({});
      return;
    }

    const dayNumbers = [...new Set(activitiesWithCoords.map(a => a.day_number))].sort((a, b) => a - b);

    const fetchAllRoutes = async () => {
      const newRoutes = {};

      for (const day of dayNumbers) {
        const dayActs = activitiesWithCoords
          .filter(a => a.day_number === day)
          .sort((a, b) => (a.position || 0) - (b.position || 0));
        if (dayActs.length >= 2) {
          const coords = dayActs.map(a => [a.latitude, a.longitude]);
          const route = await fetchRoute(coords);
          if (route) newRoutes[day] = route;
        }
      }

      for (let i = 0; i < dayNumbers.length - 1; i++) {
        const currentDayActs = activitiesWithCoords
          .filter(a => a.day_number === dayNumbers[i])
          .sort((a, b) => (a.position || 0) - (b.position || 0));
        const nextDayActs = activitiesWithCoords
          .filter(a => a.day_number === dayNumbers[i + 1])
          .sort((a, b) => (a.position || 0) - (b.position || 0));

        if (currentDayActs.length > 0 && nextDayActs.length > 0) {
          const last = currentDayActs[currentDayActs.length - 1];
          const first = nextDayActs[0];
          const route = await fetchRoute([
            [last.latitude, last.longitude],
            [first.latitude, first.longitude],
          ]);
          if (route) newRoutes[`between-${dayNumbers[i]}-${dayNumbers[i + 1]}`] = route;
        }
      }

      setRoutes(newRoutes);
    };

    fetchAllRoutes();
  }, [routeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return routes;
}
